/**
 * `documentDisplayNames.ts` — resolve a citation's raw FILE name to the
 * document name an author set in the Define app.
 *
 * A document has two names and they are not interchangeable:
 *
 * | | Where it comes from | What it is for |
 * | - | - | - |
 * | `Robodog_Manual 1.pdf` | the uploaded file, and OpenAI's `filename` on every `file_search` annotation | IDENTITY — matching, dedup, opening the file |
 * | `Robodog Manual` | the title an author sets in the Define app | DISPLAY — what a person reads |
 *
 * The Dynamo key is **`name`** — confirmed against the dev account, whose row
 * carries BOTH `fileName` (the upload) and `name` (the title).
 * `productDocumentName` reads six spellings and finds `fileName` two keys
 * before `name`, which is why the title was never reached.
 *
 * ⚠ Do NOT close that by reordering `productDocumentName`. It is IDENTITY for
 * the thumbnail S3 key (`docs/thumbnails/{fileName}.png`), `Find_and_Show_PDF`'s
 * exact match, the format badge's extension test, and the vector-store
 * `filename` attribute the Unity desktop client writes into the SAME shared
 * store. `productDocumentDisplayName` is the separate display resolver, and
 * `DocLibraryItem.displayName` carries it — the same split citations use here.
 *
 * ⚠ **This is a DISPLAY mapping and must stay at the edge.** Inside
 * `AIContextEngineModel.KnowledgeTransport` the file name is what
 * `findPdfAsync` MATCHES a requested PDF against, and what `withSources`
 * de-duplicates on; rewriting it there would make `Find_and_Show_PDF` fail to
 * find a file the user named, and would merge two documents that happen to
 * share a display name. Resolve after all matching is done — in the executor,
 * on the way to the user — and never mutate `storagePath` or `fileId`, which
 * are the identity the chip's click handler and the dedup key both use.
 */

import type { DocLibraryItem } from "./IProjectDocsModel";

/** Namespaced lower-cased lookup keys → the Define-UI document name. */
export type DocumentNameIndex = ReadonlyMap<string, string>;

/**
 * ⚠ TWO KEY SPACES, deliberately not one.
 *
 * A PATH-derived key identifies exactly one document (it is minted from the
 * row's unique `name`/`sk`), so first-writer-wins is safe there. A FILE NAME
 * does not — `manual.pdf` and scanner defaults collide across documents, which
 * is what the poison map below exists for.
 *
 * Merged into one map those guarantees leak into each other: a document whose
 * uploaded file name happens to equal ANOTHER document's path segment would
 * resolve to that other document's title, because the path key was written
 * first and the file-name pass could only skip it. That is the confident wrong
 * attribution the poison map prevents, arriving by a second door. Namespacing
 * costs nothing and makes the two spaces incapable of colliding.
 */
const K_PATH_SPACE = "path:";
const K_FILE_SPACE = "file:";

/** ``` when the key is empty, so a caller can skip it. */
function spaced(space: string, key: string): string {
  return key ? space + key : "";
}

/**
 * Trailing path segment of a storage path, lower-cased. `docs/A B.pdf` and a
 * bare `A B.pdf` both key on `a b.pdf`, which is what lets a citation carrying
 * only one of the two still resolve.
 */
function fileKeyOf(value: string | null | undefined): string {
  const trimmed = value?.trim();
  if (!trimmed) return "";
  const slash = trimmed.lastIndexOf("/");
  return (slash >= 0 ? trimmed.slice(slash + 1) : trimmed).toLowerCase();
}

function pathKeyOf(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? "";
}

/**
 * Index the loaded library under every key a citation might arrive with.
 *
 * TWO CLASSES OF KEY, and the difference decides how a collision is handled:
 *
 * - `storagePath` and its trailing segment are derived from the row's unique
 *   `name`/`sk`, so they identify one document. First writer wins is safe.
 * - `fileName` is the UPLOADED file and is NOT unique — `manual.pdf`,
 *   `scan001.pdf` and scanner defaults collide across documents. A key two
 *   documents claim is DROPPED rather than guessed at, because a confident
 *   wrong attribution inside a ranked source list is worse than the honest
 *   miss it replaces.
 *
 * ⚠ The file-name key is a LEGACY / cross-client fallback ONLY. On the primary
 * path a citation already carries the real `storage_path`
 * (`CustomerVectorStoreModel` attaches it, `annotationsToCitations` reads it),
 * so the path key hits first. It exists for vector records attached without
 * that attribute.
 */
export function buildDocumentNameIndex(
  items: ReadonlyArray<DocLibraryItem>,
): DocumentNameIndex {
  const index = new Map<string, string>();
  const fileNameKeys = new Map<string, string | null>(); // null = claimed twice

  for (const item of items) {
    const display = item.displayName?.trim() || item.fileName?.trim();
    if (!display) continue;

    for (const key of [pathKeyOf(item.storagePath), fileKeyOf(item.storagePath)]) {
      const spacedKey = spaced(K_PATH_SPACE, key);
      if (spacedKey && !index.has(spacedKey)) index.set(spacedKey, display);
    }

    const fileKey = fileKeyOf(item.fileName);
    if (fileKey) fileNameKeys.set(fileKey, fileNameKeys.has(fileKey) ? null : display);
  }

  // Poisoned keys are never written, so a shared file name resolves to nothing
  // and the citation keeps its own name. No `index.has` guard: the file space
  // is this loop's alone, and `fileNameKeys` has already settled its duplicates.
  for (const [key, display] of fileNameKeys) {
    if (display) index.set(K_FILE_SPACE + key, display);
  }
  return index;
}

/**
 * The Define-UI name for a citation, or `null` when the library does not know
 * this document — a citation for a file the current project cannot see, or a
 * library that has not loaded. `null` means "leave the caller's own name
 * alone"; it never means "no name".
 *
 * The full storage path is tried first because it is unique; the bare file
 * name second, since a `file_search` annotation often carries no path at all.
 */
export function resolveDocumentName(
  index: DocumentNameIndex,
  storagePath: string | null | undefined,
  fileName: string | null | undefined,
): string | null {
  const candidates = [
    spaced(K_PATH_SPACE, pathKeyOf(storagePath)),
    spaced(K_PATH_SPACE, fileKeyOf(storagePath)),
    spaced(K_FILE_SPACE, fileKeyOf(fileName)),
  ];
  for (const key of candidates) {
    if (!key) continue;
    const hit = index.get(key);
    if (hit) return hit;
  }
  return null;
}

/**
 * How a document is named in AGENT-FACING text — `Robodog Manual (file:
 * Robodog_Manual 1.pdf)`.
 *
 * ⚠ BOTH names, deliberately, and this is the 2026-09-04 direction call kept
 * rather than reversed. That call said the presentation should show the title;
 * it also kept the raw file name in the model's own input, because a title is
 * frequently LESS specific than its filename and the filename is the word the
 * agent uses to notice it is holding the wrong document. Handing over only the
 * title would take that word away — so the title is ADDED here, never
 * substituted, and `fileName` stays the identity it has always been.
 *
 * The user-visible defect this closes (2026-09-21): asked "do you have a user
 * manual?", Spark answered `I found a user manual titled "Robodog_Manual 1.pdf"`
 * while the chip beneath it read `Robodog Manual`. The title had simply never
 * reached the agent — only the chips were ever relabelled.
 *
 * Collapses to the single name when there is only one to give, or when the two
 * are the same string, so a library with no authored titles reads exactly as it
 * did before.
 */
export function agentFacingDocumentName(
  displayName: string | null | undefined,
  fileName: string | null | undefined,
): string {
  const title = displayName?.trim() ?? "";
  const file = fileName?.trim() ?? "";
  if (!title || title === file) return file || title;
  if (!file) return title;
  return `${title} (file: ${file})`;
}
